import axios from "axios";
import { toast } from "sonner";

// Resolve API base URL.
// Strategy:
//   1. If REACT_APP_BACKEND_URL is set AND its host matches the current page host,
//      use it (handy for explicit dev overrides pointing to localhost backend).
//   2. Otherwise (production custom domain, or env unset) → use window.location.origin
//      so requests stay on the same origin and ingress routes /api/* to backend.
function resolveBackendUrl() {
  if (typeof window === "undefined") return process.env.REACT_APP_BACKEND_URL || "";
  const origin = window.location.origin;
  const env = (process.env.REACT_APP_BACKEND_URL || "").trim();
  if (!env) return origin;
  try {
    const envHost = new URL(env).host;
    return envHost === window.location.host ? env : origin;
  } catch {
    return origin;
  }
}
const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("kopsebra_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global response interceptor — surface unhandled / network errors as toasts
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error?.config || {};
    // Allow callers to opt-out of auto-toast by passing { silent: true } to axios call
    if (cfg.silent) return Promise.reject(error);

    // Auth — redirect handled by AuthContext via 401 on /me; don't toast here for /auth/me background calls
    if (error?.response?.status === 401 && cfg.url?.includes("/auth/me")) {
      return Promise.reject(error);
    }

    // Network error / no response
    if (!error.response) {
      toast.error("Tidak dapat terhubung ke server. Periksa koneksi Anda.");
      return Promise.reject(error);
    }

    // Skip toast for routes that handle errors locally (login/register show inline errors)
    const url = cfg.url || "";
    const inlineRoutes = ["/auth/login", "/auth/register"];
    if (inlineRoutes.some((r) => url.includes(r))) return Promise.reject(error);

    const detail = error.response.data?.detail;
    const message = formatApiError(error);
    const status = error.response.status;
    if (status === 401) {
      toast.error("Sesi berakhir. Silakan login ulang.");
      localStorage.removeItem("kopsebra_token");
      // soft redirect — RootRedirect will pick up
      if (window.location.pathname !== "/") window.location.href = "/";
    } else if (status === 403) {
      toast.error(message || "Anda tidak memiliki akses untuk aksi ini.");
    } else if (status >= 500) {
      toast.error("Terjadi kesalahan pada server. Coba lagi sebentar.");
    } else if (detail) {
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) {
    if (err?.code === "ECONNABORTED") return "Permintaan terlalu lama. Coba lagi.";
    if (err?.message?.includes("Network")) return "Gagal terhubung ke server.";
    return err?.message || "Terjadi kesalahan";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return JSON.stringify(detail);
}

export function formatRupiah(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(num);
}

export function formatDate(value, opts = { day: "2-digit", month: "short", year: "numeric" }) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("id-ID", opts);
  } catch {
    return String(value);
  }
}
